import React, { useState } from "react";
import { Container, Typography, Box, Grid, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { useTheme, keyframes } from "@mui/material/styles";
import PoolIcon from "@mui/icons-material/Pool";
import HolidayVillageIcon from "@mui/icons-material/HolidayVillage";
import LandscapeIcon from "@mui/icons-material/Landscape";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VideoCard from "../components/VideoCard";
import Reveal from "../components/Reveal";
import { FullBleed, Section, ZoomImage, fadeUp } from "../components/layout";
import CONTACT_INFO from "../constants/contactInfo";
import logo from "../assets/logo.png";

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(8px); }
`;

const IMAGES = {
  heroFallback:
    "https://ik.imagekit.io/cabanasdelcampo/videos/Captura%20de%20pantalla%202025-01-22%20a%20la(s)%2015.15.31.png?updatedAt=1737569765896",
  heroVideo:
    "https://ik.imagekit.io/cabanasdelcampo/videos/drone-home.mp4?updatedAt=1737569565612",
  panoramica:
    "https://ik.imagekit.io/cabanasdelcampo/exteriores/20250113_115619.jpg?updatedAt=1737417547967",
};

const FEATURES = [
  {
    icon: PoolIcon,
    overline: "Relax",
    title: "Piscina y Solarium",
    text: "Disfruta de nuestro exclusivo espacio de relax con una hermosa piscina rodeada de un amplio solarium. El lugar perfecto para refrescarte en los días cálidos mientras te deleitas con las vistas de las sierras.",
    image:
      "https://ik.imagekit.io/cabanasdelcampo/pileta/20250113_113845.jpg?updatedAt=1737417503152",
    alt: "Piscina y Solarium",
  },
  {
    icon: LandscapeIcon,
    overline: "Aventura",
    title: "Paseos en cuatriciclo",
    text: "Te ofrecemos paseos guiados en cuatriciclo, visitando distintos puntos panorámicos de la zona, para que le sumes aventura a tu estadía. Consultanos por precios y disponibilidad por mensaje privado.",
    image:
      "https://ik.imagekit.io/cabanasdelcampo/exteriores/20250113_113039.jpg?updatedAt=1737417550889",
    alt: "Paseo en cuatriciclo",
    action: {
      label: "Mirá el video",
      href: "https://youtube.com/shorts/9dgfU4No36Q",
    },
    dark: true,
  },
  {
    icon: HolidayVillageIcon,
    overline: "Las cabañas",
    title: "Confort y Naturaleza",
    text: "Nuestras cabañas combinan el encanto rústico con todas las comodidades modernas que necesitas para una estadía inolvidable. Cada detalle está pensado para que tu experiencia sea perfecta.",
    image:
      "https://ik.imagekit.io/cabanasdelcampo/interiores/20250113_101105.jpg?updatedAt=1737417457374",
    alt: "Interior de la cabaña",
  },
];

// Grilla tipo bento: posición de cada foto en mobile (2 col) y desktop (4 col)
const GALLERY = [
  {
    src: "https://ik.imagekit.io/cabanasdelcampo/exteriores/IMG-20250102-WA0053.jpg?updatedAt=1737325754093",
    alt: "Vista principal de las cabañas",
    sx: { gridColumn: { xs: "span 2", md: "1 / 3" }, gridRow: { md: "1 / 3" } },
  },
  {
    src: "https://ik.imagekit.io/cabanasdelcampo/alrededores/20241027_133414.jpg?updatedAt=1737325675800",
    alt: "Vista del río",
    sx: { gridColumn: { md: "3" }, gridRow: { md: "1" } },
  },
  {
    src: "https://ik.imagekit.io/cabanasdelcampo/interiores/20250113_101930.jpg?updatedAt=1737417456923",
    alt: "Interior de la cabaña",
    sx: { gridColumn: { md: "4" }, gridRow: { md: "1 / 3" } },
  },
  {
    src: "https://ik.imagekit.io/cabanasdelcampo/exteriores/WhatsApp%20Image%202026-06-17%20at%2011.38.29.jpeg?updatedAt=1781707375178",
    alt: "Vista exterior de la cabaña",
    position: "center 65%",
    sx: { gridColumn: { md: "3" }, gridRow: { md: "2" } },
  },
  {
    src: "https://ik.imagekit.io/cabanasdelcampo/pileta/20250113_115540.jpg?updatedAt=1737417503079",
    alt: "Piscina y área de relax",
    sx: { gridColumn: { md: "1 / 3" } },
  },
  {
    src: "https://ik.imagekit.io/cabanasdelcampo/exteriores/DCS_3124_1.jpg?updatedAt=1760707738623",
    alt: "Fogón con bancos entre los pinos",
    position: "center 60%",
    sx: { gridColumn: { xs: "span 2", md: "3 / 5" } },
  },
];

const STATS = [
  { value: "16 km", label: "de Villa General Belgrano" },
  { value: "15 km", label: "de La Cumbrecita" },
  { value: "4", label: "personas por cabaña" },
];

const Home = () => {
  const theme = useTheme();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const green = theme.palette.secondary.dark;

  return (
    <Box>
      {/* Hero */}
      <FullBleed
        sx={{
          position: "relative",
          height: "100svh",
          minHeight: 560,
          mt: "-20px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
        }}
      >
        <Box
          component="img"
          src={IMAGES.heroFallback}
          alt=""
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: videoLoaded ? 0 : 1,
            transition: "opacity 1s ease",
            zIndex: 1,
          }}
        />
        <Box
          component="video"
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
        >
          <source src={IMAGES.heroVideo} type="video/mp4" />
        </Box>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 70%), linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 45%, rgba(0,30,15,0.7) 100%)",
          }}
        />

        <Container
          sx={{
            position: "relative",
            zIndex: 3,
            textAlign: "center",
            animation: `${fadeUp} 1s cubic-bezier(.2,.7,.2,1) both`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          <Box>
            <Box
              component="img"
              src={logo}
              alt="Logo Cabañas del Campo"
              sx={{
                width: { xs: 120, md: 160 },
                height: "auto",
                mb: 2,
                filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.45))",
              }}
            />
            <Typography
              variant="overline"
              component="p"
              sx={{ color: theme.palette.primary.light }}
            >
              Valle de Calamuchita · Córdoba
            </Typography>
            <Typography variant="h1" component="h1" sx={{ color: "white", mt: 1, mb: 2 }}>
              Cabañas del Campo
            </Typography>
            <Typography
              sx={{
                color: "white",
                fontSize: { xs: "1.1rem", md: "1.35rem" },
                fontWeight: 400,
                textShadow: "0 1px 16px rgba(0,0,0,0.6)",
                maxWidth: 560,
                mx: "auto",
                mb: 5,
              }}
            >
              Tu escape perfecto en Athos Pampa, entre sierras, arroyo y
              naturaleza.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button variant="contained" size="large" component={Link} to="/alojamiento">
                Conocé las cabañas
              </Button>
              <Button
                variant="outlined"
                size="large"
                href={CONTACT_INFO.reservarLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.7)",
                  backdropFilter: "blur(6px)",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                Consultar disponibilidad
              </Button>
            </Box>
          </Box>
        </Container>

        <Box
          component="a"
          href="#bienvenidos"
          aria-label="Ver más"
          sx={{
            position: "absolute",
            bottom: 24,
            left: "50%",
            ml: "-20px",
            zIndex: 3,
            color: "white",
            opacity: 0.8,
            animation: `${bounce} 2s ease-in-out infinite`,
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 40 }} />
        </Box>
      </FullBleed>

      {/* Bienvenida */}
      <Section id="bienvenidos" sx={{ scrollMarginTop: 72 }}>
        <Container>
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Reveal>
                <Typography variant="overline" color="primary">
                  Bienvenidos
                </Typography>
                <Typography variant="h2" sx={{ mt: 1, mb: 4 }}>
                  Un paraíso natural en el corazón de las sierras
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
                  En Cabañas del Campo, situadas en el Valle de Calamuchita, una
                  región reconocida como reserva natural, te invitamos a vivir
                  una experiencia única. Aquí podrás conectarte con todos tus
                  sentidos mientras disfrutas de días rodeados de tranquilidad y
                  naturaleza.
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: "text.secondary" }}>
                  Con el arroyo a pocos pasos, nuestras cabañas ofrecen el
                  refugio perfecto para desconectarte del ritmo diario y renovar
                  tus energías, a minutos de Villa General Belgrano y La
                  Cumbrecita.
                </Typography>
              </Reveal>
              <Reveal delay={150}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                    mt: 5,
                    pt: 4,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {STATS.map((stat) => (
                    <Box key={stat.label}>
                      <Typography
                        sx={{
                          fontFamily: theme.typography.h1.fontFamily,
                          fontSize: { xs: "1.8rem", md: "2.4rem" },
                          fontWeight: 600,
                          color: theme.palette.secondary.main,
                          lineHeight: 1.1,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.4 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={5}>
              <Reveal delay={200}>
                <VideoCard videoUrl="https://www.youtube.com/embed/w2HS3BaA0LA?autoplay=1&mute=1" />
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Imagen panorámica */}
      <FullBleed
        sx={{
          position: "relative",
          height: { xs: "60vh", md: "75vh" },
          backgroundImage: `url("${IMAGES.panoramica}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: { xs: "scroll", md: "fixed" },
          display: "flex",
          alignItems: "flex-end",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0) 60%)",
          },
        }}
      >
        <Container sx={{ position: "relative", pb: { xs: 5, md: 8 } }}>
          <Reveal>
            <Typography
              sx={{
                fontFamily: theme.typography.h1.fontFamily,
                fontStyle: "italic",
                fontWeight: 400,
                color: "white",
                fontSize: { xs: "1.8rem", md: "3rem" },
                maxWidth: 720,
                lineHeight: 1.2,
              }}
            >
              Donde la serenidad y la naturaleza se fusionan.
            </Typography>
          </Reveal>
        </Container>
      </FullBleed>

      {/* Lo que te espera */}
      {FEATURES.map((feature, index) => {
        const Icon = feature.icon;
        const imageFirst = index % 2 === 1;
        return (
          <Section
            key={feature.title}
            sx={{
              backgroundColor: feature.dark ? green : "transparent",
              py: { xs: 8, md: feature.dark ? 14 : 10 },
            }}
          >
            <Container>
              <Grid
                container
                spacing={{ xs: 4, md: 10 }}
                alignItems="center"
                direction={{ xs: "column-reverse", md: imageFirst ? "row-reverse" : "row" }}
              >
                <Grid item xs={12} md={5}>
                  <Reveal>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        mb: 3,
                        backgroundColor: feature.dark
                          ? "rgba(255,255,255,0.1)"
                          : theme.palette.primary.main + "1f",
                      }}
                    >
                      <Icon sx={{ color: feature.dark ? theme.palette.primary.light : "primary.main" }} />
                    </Box>
                    <Typography
                      variant="overline"
                      sx={{ color: feature.dark ? theme.palette.primary.light : "primary.main" }}
                    >
                      {feature.overline}
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{ mt: 1, mb: 3, color: feature.dark ? "white" : "text.primary" }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: feature.dark ? "rgba(255,255,255,0.85)" : "text.secondary" }}
                    >
                      {feature.text}
                    </Typography>
                    {feature.action && (
                      <Button
                        variant="contained"
                        href={feature.action.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<PlayArrowIcon />}
                        sx={{ mt: 4 }}
                      >
                        {feature.action.label}
                      </Button>
                    )}
                  </Reveal>
                </Grid>
                <Grid item xs={12} md={7} sx={{ width: "100%" }}>
                  <Reveal delay={150}>
                    <ZoomImage sx={{ height: { xs: 280, md: 460 } }}>
                      <img src={feature.image} alt={feature.alt} loading="lazy" />
                    </ZoomImage>
                  </Reveal>
                </Grid>
              </Grid>
            </Container>
          </Section>
        );
      })}

      {/* Galería */}
      <Section sx={{ backgroundColor: theme.palette.sand.light }}>
        <Container>
          <Reveal>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "flex-end" },
                justifyContent: "space-between",
                gap: 2,
                mb: 5,
              }}
            >
              <Box>
                <Typography variant="overline" color="primary">
                  Galería
                </Typography>
                <Typography variant="h2" sx={{ mt: 1 }}>
                  Un vistazo a tu estadía
                </Typography>
              </Box>
              <Button component={Link} to="/galeria" endIcon={<ArrowForwardIcon />}>
                Ver galería completa
              </Button>
            </Box>
          </Reveal>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gridAutoRows: { xs: 160, md: 240 },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {GALLERY.map((img, index) => (
              <Reveal key={img.src} delay={index * 80} sx={img.sx}>
                <ZoomImage
                  sx={{ height: "100%", borderRadius: "16px", "& img": { objectPosition: img.position || "center" } }}
                >
                  <img src={img.src} alt={img.alt} loading="lazy" />
                </ZoomImage>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Section>

      {/* Cierre */}
      <Section sx={{ backgroundColor: green, mb: "-20px", textAlign: "center" }}>
        <Container maxWidth="md">
          <Reveal>
            <Typography variant="overline" sx={{ color: theme.palette.primary.light }}>
              Tu próxima escapada
            </Typography>
            <Typography variant="h2" sx={{ color: "white", mt: 1, mb: 3 }}>
              ¿Listo para desconectarte?
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.85)", mb: 5, fontSize: "1.1rem" }}>
              Escribinos y te ayudamos a planear tu estadía en las sierras.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                href={CONTACT_INFO.reservarLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Consultar disponibilidad
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/alojamiento"
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.6)",
                  "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.08)" },
                }}
              >
                Conocé las cabañas
              </Button>
            </Box>
          </Reveal>
        </Container>
      </Section>
    </Box>
  );
};

export default Home;
